/**
 * Centralized Messages Configuration
 * 
 * This file contains all user-friendly messages for the application.
 * Modify messages here to update them across the entire application.
 */

// ==================== TEAM MEMBER MESSAGES ====================
export const TEAMMEMBER_MESSAGES = {
  create: {
    success: "Team member created successfully!",
    error: "Failed to create team member. Please try again.",
    errorGeneric: "An error occurred while creating the team member.",
  },
  update: {
    success: "Team member updated successfully!",
    error: "Failed to update team member. Please try again.",
    errorGeneric: "An error occurred while updating the team member.",
  },
  delete: {
    success: "Team member deleted successfully!",
    error: "Failed to delete team member. Please try again.",
    errorGeneric: "An error occurred while deleting the team member.",
  },
  fetch: {
    success: "Team members loaded successfully!",
    error: "Failed to load team members. Please try again.",
    errorGeneric: "An error occurred while fetching team members.",
  },
  fetchSingle: {
    success: "Team member loaded successfully!",
    error: "Failed to load team member. Please try again.",
    errorGeneric: "An error occurred while fetching team member.",
  },
} as const;

// ==================== AGENT MESSAGES ====================
export const AGENT_MESSAGES = {
  create: {
    success: "Agent created successfully!",
    error: "Failed to create agent. Please try again.",
    errorGeneric: "An error occurred while creating the agent.",
  },
  update: {
    success: "Agent updated successfully!",
    error: "Failed to update agent. Please try again.",
    errorGeneric: "An error occurred while updating the agent.",
  },
  delete: {
    success: "Agent deleted successfully!",
    error: "Failed to delete agent. Please try again.",
    errorGeneric: "An error occurred while deleting the agent.",
  },
  fetch: {
    success: "Agents loaded successfully!",
    error: "Failed to load agents. Please try again.",
    errorGeneric: "An error occurred while fetching agents.",
  },
  fetchSingle: {
    success: "Agent loaded successfully!",
    error: "Failed to load agent. Please try again.",
    errorGeneric: "An error occurred while fetching agent.",
  },
  // Additional fetch operations
  fetchOffices: {
    success: "Offices loaded successfully!",
    error: "Failed to load offices. Please try again.",
    errorGeneric: "An error occurred while fetching offices.",
  },
  fetchBrokerageFirms: {
    success: "Brokerage firms loaded successfully!",
    error: "Failed to load brokerage firms. Please try again.",
    errorGeneric: "An error occurred while fetching brokerage firms.",
  },
  fetchParentHierarchy: {
    success: "Parent hierarchy loaded successfully!",
    error: "Failed to load parent hierarchy. Please try again.",
    errorGeneric: "An error occurred while fetching parent hierarchy.",
  },
  fetchImage: {
    success: "Image loaded successfully!",
    error: "Failed to load image. Please try again.",
    errorGeneric: "An error occurred while fetching image.",
  },
} as const;

// ==================== BROKERAGE FIRM MESSAGES ====================
export const BROKERAGE_FIRM_MESSAGES = {
  create: {
    success: "Brokerage firm added successfully!",
    error: "Failed to add brokerage firm. Please try again.",
    errorGeneric: "An error occurred while adding the brokerage firm.",
  },
  update: {
    success: "Brokerage firm updated successfully!",
    error: "Failed to update brokerage firm. Please try again.",
    errorGeneric: "An error occurred while updating the brokerage firm.",
  },
  delete: {
    success: "Brokerage firm deleted successfully!",
    error: "Failed to delete brokerage firm. Please try again.",
    errorGeneric: "An error occurred while deleting the brokerage firm.",
  },
  fetch: {
    success: "Brokerage firms loaded successfully!",
    error: "Failed to load brokerage firms. Please try again.",
    errorGeneric: "An error occurred while fetching brokerage firms.",
  },
  fetchSingle: {
    success: "Brokerage firm loaded successfully!",
    error: "Failed to load brokerage firm. Please try again.",
    errorGeneric: "An error occurred while fetching brokerage firm.",
  },
  activateLicense: {
    success: "License activated successfully!",
    error: "Failed to activate license. Please try again.",
    errorGeneric: "An error occurred while activating the license.",
  },
  deactivateLicense: {
    success: "License deactivated successfully!",
    error: "Failed to deactivate license. Please try again.",
    errorGeneric: "An error occurred while deactivating the license.",
  },
} as const;

// ==================== ADDRESS ADD/EDIT MESSAGES ====================
export const ADDRESSADDEDIT_MESSAGES = {
  fetch: {
    success: "Address loaded successfully!",
    error: "Failed to load address. Please try again.",
    errorGeneric: "An error occurred while fetching address.",
  },
  create: {
    success: "Address added successfully!",
    error: "Failed to add address. Please try again.",
    errorGeneric: "An error occurred while adding the address.",
  },
  update: {
    success: "Address updated successfully!",
    error: "Failed to update address. Please try again.",
    errorGeneric: "An error occurred while updating the address.",
  },
  fetchError: "Error fetching address",
  addError: "Failed to add address",
  updateError: "Failed to update address",
} as const;

// ==================== AGENT/OFFICE PRODUCT ADD/EDIT MESSAGES ====================
export const AGENTOFFICEPRODUCTADDEDIT_MESSAGES = {
  create: {
    success: "Product created successfully!",
    error: "Failed to create product. Please try again.",
    errorGeneric: "An error occurred while creating the product.",
  },
  update: {
    success: "Product updated successfully!",
    error: "Failed to update product. Please try again.",
    errorGeneric: "An error occurred while updating the product.",
  },
  fetch: {
    success: "Product loaded successfully!",
    error: "Failed to load product. Please try again.",
    errorGeneric: "An error occurred while fetching product.",
  },
  validation: {
    warehouseDetailsRequired: "Please fill all required warehouse details.",
    quantityRequired: "Please add quantity for at least one warehouse.",
    officeSelectionRequired: "Office selection is required",
    agentIdRequired: "Agent ID is required",
  },
  image: {
    deleteFailed: "Some removed images could not be deleted.",
  },
  product: {
    createFailed: "Failed to create product",
    updateFailed: "Failed to save data",
    productIdNotFound: "Product created but product_id not found in response",
    linkInstallerFailed: "Failed to link installer",
    acceptFailed: "Failed to accept product",
  },
  category: {
    fetchError: "Error fetching category data. Please try again later.",
  },
} as const;

// ==================== WAREHOUSE MESSAGES ====================
export const WAREHOUSE_MESSAGES = {
  create: {
    success: "Warehouse added successfully!",
    error: "Failed to add warehouse. Please try again.",
    errorGeneric: "An error occurred while adding the warehouse.",
  },
  update: {
    success: "Warehouse updated successfully!",
    error: "Failed to update warehouse. Please try again.",
    errorGeneric: "An error occurred while updating the warehouse.",
  },
  delete: {
    success: "Warehouse deleted successfully!",
    error: "Failed to delete warehouse. Please try again.",
    errorGeneric: "An error occurred while deleting the warehouse.",
  },
  fetch: {
    success: "Warehouses loaded successfully!",
    error: "Failed to load warehouses. Please try again.",
    errorGeneric: "An error occurred while fetching warehouses.",
  },
  fetchSingle: {
    success: "Warehouse loaded successfully!",
    error: "Failed to load warehouse. Please try again.",
    errorGeneric: "An error occurred while fetching warehouse data.",
  },
} as const;

// ==================== ZONE MESSAGES ====================
export const ZONE_MESSAGES = {
  create: {
    success: "Zone created successfully!",
    error: "Failed to create zone. Please try again.",
    errorGeneric: "An error occurred while creating the zone.",
  },
  update: {
    success: "Zone updated successfully!",
    error: "Failed to update zone. Please try again.",
    errorGeneric: "An error occurred while updating the zone.",
  },
  delete: {
    success: "Zone deleted successfully!",
    error: "Failed to delete zone. Please try again.",
    errorGeneric: "An error occurred while deleting the zone.",
  },
  fetch: {
    success: "Zones loaded successfully!",
    error: "Failed to load zones. Please try again.",
    errorGeneric: "An error occurred while fetching zones.",
  },
  validation: {
    nameRequired: "Zone name is required",
    notFound: "Zone not found",
  },
  deleteError: {
    hasRacks: "This zone has racks assigned. Please delete the racks first before deleting the zone.",
  },
} as const;

// ==================== RACK MESSAGES ====================
export const RACK_MESSAGES = {
  create: {
    success: "Rack created successfully!",
    error: "Failed to create rack. Please try again.",
    errorGeneric: "An error occurred while creating the rack.",
  },
  update: {
    success: "Rack updated successfully!",
    error: "Failed to update rack. Please try again.",
    errorGeneric: "An error occurred while updating the rack.",
  },
  delete: {
    success: "Rack deleted successfully!",
    error: "Failed to delete rack. Please try again.",
    errorGeneric: "An error occurred while deleting the rack.",
  },
  fetch: {
    success: "Racks loaded successfully!",
    error: "Failed to load racks. Please try again.",
    errorGeneric: "An error occurred while fetching racks.",
  },
  validation: {
    nameRequired: "Rack name is required",
    zoneIdMissing: "Zone ID is missing",
    warehouseIdMissing: "Warehouse ID is missing",
    notFound: "Rack not found",
  },
  deleteError: {
    hasBins: "This rack has associated bins. Please delete the bins first.",
  },
} as const;

// ==================== BIN MESSAGES ====================
export const BIN_MESSAGES = {
  create: {
    success: "Bin created successfully!",
    error: "Failed to create bin. Please try again.",
    errorGeneric: "An error occurred while creating the bin.",
  },
  update: {
    success: "Bin updated successfully!",
    error: "Failed to update bin. Please try again.",
    errorGeneric: "An error occurred while updating the bin.",
  },
  delete: {
    success: "Bin deleted successfully!",
    error: "Failed to delete bin. Please try again.",
    errorGeneric: "An error occurred while deleting the bin.",
  },
  fetch: {
    success: "Bins loaded successfully!",
    error: "Failed to load bins. Please try again.",
    errorGeneric: "An error occurred while fetching bins.",
  },
  validation: {
    nameRequired: "Bin name is required",
    volumeRequired: "Volume must be a number greater than 0",
    notFound: "Bin not found",
  },
} as const;


// ==================== SignageSimple PRODUCT ADD/EDIT MESSAGES ====================
export const SignageSimpleProductADDEDIT_MESSAGES = {
  create: {
    success: "Product created successfully!",
    error: "Failed to create product. Please try again.",
    errorGeneric: "An error occurred while creating the product.",
  },
  update: {
    success: "Product updated successfully!",
    error: "Failed to update product. Please try again.",
    errorGeneric: "An error occurred while updating the product.",
  },
  fetch: {
    success: "Product loaded successfully!",
    error: "Failed to load product. Please try again.",
    errorGeneric: "An error occurred while fetching product.",
  },
  validation: {
    warehouseDetailsRequired: "Please fill all required warehouse details.",
    quantityRequired: "Please add quantity for at least one warehouse.",
    officeSelectionRequired: "Office selection is required",
    agentIdRequired: "Agent ID is required",
    warehousehierarchy: "Failed to load Warehouse Zones,Racks,Bins. Please try again.",
  },
  image: {
    deleteFailed: "Some removed images could not be deleted.",
  },
  product: {
    createFailed: "Failed to create product",
    updateFailed: "Failed to save data",
    productIdNotFound: "Product created but product_id not found in response",
    linkInstallerFailed: "Failed to link installer",
    acceptFailed: "Failed to accept product",
  },
  category: {
    fetchError: "Error fetching category data. Please try again later.",
  },
} as const;

// ==================== GENERIC/COMMON MESSAGES ====================
export const COMMON_MESSAGES = {
  networkError: "Network error. Please check your connection and try again.",
  unauthorized: "You are not authorized to perform this action.",
  forbidden: "Access denied. You don't have permission to perform this action.",
  notFound: "The requested resource was not found.",
  serverError: "Server error. Please try again later.",
  validationError: "Please check your input and try again.",
  unknownError: "An unexpected error occurred. Please try again.",
  saveError: "Failed to save data. Please try again.",
  loadError: "Failed to load data. Please try again.",
  deleteConfirm: "Are you sure you want to delete this item?",
} as const;

// ==================== MESSAGE TYPE DEFINITIONS ====================
export type EntityType = "teammember" | "agent" | "brokeragefirm" ;
export type OperationType = "create" | "update" | "delete" | "fetch" | "fetchSingle";

// ==================== MESSAGE MAPPING ====================
export const MESSAGE_MAP: Record<
  EntityType,
  Record<OperationType, { success: string; error: string; errorGeneric: string }>
> = {
  teammember: TEAMMEMBER_MESSAGES,
  agent: AGENT_MESSAGES,
  brokeragefirm: BROKERAGE_FIRM_MESSAGES,
} as const;

// ==================== BACKEND ERROR MAPPING ====================
/**
 * Maps common backend error messages/codes to user-friendly messages
 */
export const ERROR_CODE_MAP: Record<string, string> = {
  // Network errors
  "Network request failed": COMMON_MESSAGES.networkError,
  "Failed to fetch": COMMON_MESSAGES.networkError,
  
  // HTTP status code messages
  "401": COMMON_MESSAGES.unauthorized,
  "403": COMMON_MESSAGES.forbidden,
  "404": COMMON_MESSAGES.notFound,
  "500": COMMON_MESSAGES.serverError,
  
  // Common backend error patterns
  "Unauthorized": COMMON_MESSAGES.unauthorized,
  "Forbidden": COMMON_MESSAGES.forbidden,
  "Not Found": COMMON_MESSAGES.notFound,
  "Validation Error": COMMON_MESSAGES.validationError,
  "Internal Server Error": COMMON_MESSAGES.serverError,
  
  // Add more specific backend error mappings as needed
  "Failed to save data": COMMON_MESSAGES.saveError,
  "Failed to load data": COMMON_MESSAGES.loadError,
};

